import { AssessmentSelectDto, AssessmentType, DomainSecondarySelectDto, SurveyStatus } from '@@types/api.types';
import { AssessmentHistoryChartComponent } from '@components/shared/assessment-history-chart.component';
import { Tile } from '@components/shared/tile.component';
import { useAssessmentsQueryOptions } from '@queries/assessment.query';
import { useGetSurvey, useGetSurveys } from '@queries/survey.query';
import { useGetTechnologyUsesWithAssessments } from '@queries/technology-uses.query';
import { useGetAssessmentHistory, useGetTechnology, useGetTechnologyTrlAssessment } from '@queries/technology.query';
import { useQuery } from '@tanstack/react-query';
import { isLatestAssessmentForUseDomain, isMilitaryTrlAssessment } from '@utils/assessment.util';
import { getTrlBucketForScore } from '@utils/trl-buckets.util';
import { FC, useMemo, useState } from 'react';
import { AccordionTile } from '../../shared/accordion-tile.component';
import { StartTRLModal } from '../../technology-start-rapid-trl-modal.component';
import { MaInfoTile } from './ma-info-tile.component';
import { SimilarTechnologiesTile } from './similar-technologies-tile.component';
import { TrlAnalysisTile } from './trl-analysis-tile.component';
import { TrlCharacteristicsTile } from './trl-characteristics-tile.component';
import { TrlScoreTile } from './trl-score-tile.component';

type IndividualTrlContentProps = {
  assessmentId: string | null;
  canEdit: boolean;
  technologyId: string;
};

const getLatestAssessment = (assessments: AssessmentSelectDto[]): AssessmentSelectDto | null => {
  if (assessments.length === 0) return null;

  return assessments.reduce<AssessmentSelectDto>((latest, assessment) => {
    const latestDate = new Date(latest.assessmentDate ?? latest.createdAt).getTime();
    const assessmentDate = new Date(assessment.assessmentDate ?? assessment.createdAt).getTime();
    return assessmentDate > latestDate ? assessment : latest;
  }, assessments[0]);
};

export const IndividualTrlContent: FC<IndividualTrlContentProps> = (props) => {
  const { assessmentId, canEdit, technologyId } = props;

  const [isTrlModalOpen, setIsTrlModalOpen] = useState(false);

  const { data: technology } = useGetTechnology(technologyId);

  // Fetch all TRL assessments for historical view
  const assessmentQueryOptions = useAssessmentsQueryOptions({
    technologyId,
    type: [AssessmentType.TRL],
  });
  const { data: allAssessments = [], isPending: isAllAssessmentsLoading } = useQuery(assessmentQueryOptions);

  // Fetch featured assessment
  const { data: featuredAssessment, isPending: isFeaturedLoading } = useGetTechnologyTrlAssessment(technologyId);
  const latestAssessment = useMemo(() => getLatestAssessment(allAssessments), [allAssessments]);

  // Fetch draft TRL surveys
  const { data: draftSurveys = [] } = useGetSurveys({
    status: SurveyStatus.DRAFT,
    technologyId,
  });
  const draftTrlSurvey = draftSurveys.find((s) => s.assessmentType === AssessmentType.TRL) ?? null;

  // Determine which assessment to display
  const selectedAssessment: AssessmentSelectDto | null = useMemo(() => {
    if (!assessmentId) {
      return featuredAssessment ?? latestAssessment ?? null;
    }
    return allAssessments.find((a) => a.id === assessmentId) ?? featuredAssessment ?? latestAssessment ?? null;
  }, [assessmentId, allAssessments, featuredAssessment, latestAssessment]);

  // Fetch survey for the selected assessment
  const { data: survey, isPending: isSurveyLoading } = useGetSurvey(selectedAssessment?.surveyId);

  const { data: assessmentHistory } = useGetAssessmentHistory(technologyId);

  // Fetch technology uses to get subdomains
  const { data: technologyUses } = useGetTechnologyUsesWithAssessments(technologyId);

  // Get subdomains for the selected assessment
  const subdomains: DomainSecondarySelectDto[] = useMemo(() => {
    if (!selectedAssessment || !technologyUses) return [];

    const use = technologyUses.find((u) => u.id === selectedAssessment.use.id);
    const domain = use?.domains.find((d) => d.id === selectedAssessment.domain?.id);

    return domain?.subdomains ?? [];
  }, [selectedAssessment, technologyUses]);

  const trlAnalysis = selectedAssessment?.latestTrlAnalysis ?? null;
  const similarTechnologies = trlAnalysis?.similarTechnologiesSummary ?? null;

  // Only the most recent assessment of its use/domain pair may generate.
  // See docs/iterations/AZ-384/AZ-384.DECISIONS.md (D11).
  const isLatestAssessment = useMemo(() => {
    if (!selectedAssessment) return false;
    return isLatestAssessmentForUseDomain({ assessment: selectedAssessment, assessments: allAssessments });
  }, [allAssessments, selectedAssessment]);

  const isLoading = isAllAssessmentsLoading || isFeaturedLoading;
  const isMilitaryTrl = useMemo(() => {
    if (!selectedAssessment) return false;
    return isMilitaryTrlAssessment({ subtype: selectedAssessment.subtype, type: selectedAssessment.type });
  }, [selectedAssessment]);
  const isSurveyDataLoading = Boolean(selectedAssessment?.surveyId && isSurveyLoading);
  const trlBucket = useMemo(() => getTrlBucketForScore(selectedAssessment?.score), [selectedAssessment?.score]);

  const trlDescription =
    'Technology Readiness Level (TRL) is a measurement system used to assess the maturity of a particular technology. The scale ranges from 1 to 9, where TRL 1 represents basic principles observed and TRL 9 represents actual system proven through successful mission operations.';

  // Show only description accordion if no selected assessment
  // Empty state CTA is now in sidebar via SurveyCTA
  if (!isLoading && !selectedAssessment) {
    return (
      <AccordionTile
        content={trlDescription}
        title="Technology Readiness Level"
        value="trl-description"
      />
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        {/* Technology Readiness Description */}
        <AccordionTile
          content={trlDescription}
          title="Technology Readiness Level"
          value="trl-description"
        />

        {selectedAssessment && assessmentHistory && (
          <Tile>
            <AssessmentHistoryChartComponent
              assessmentType={AssessmentType.TRL}
              historyData={assessmentHistory}
              selectedUseDomain={{ domainId: selectedAssessment.domain.id, useId: selectedAssessment.use.id }}
            />
          </Tile>
        )}

        {/* Assessment Content */}
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
          {/* Left Column */}
          <div className="flex flex-col gap-4">
            <TrlScoreTile
              assessment={selectedAssessment}
              bucket={trlBucket}
              canManageAssessments={canEdit}
              draftSurvey={draftTrlSurvey}
              isLoading={isLoading}
              onStartNew={canEdit ? () => setIsTrlModalOpen(true) : undefined}
              subdomains={subdomains}
              survey={survey ?? null}
              technologyId={technologyId}
            />
            <TrlCharacteristicsTile
              isLoading={isSurveyDataLoading}
              survey={survey ?? null}
            />
          </div>

          {/* Right Column */}
          <div className="flex flex-col gap-4">
            {survey?.missionAssessmentId && (
              <MaInfoTile
                isMilitaryTrl={isMilitaryTrl}
                missionAssessmentId={survey.missionAssessmentId}
                technologyId={technologyId}
              />
            )}
            <TrlAnalysisTile
              analysis={trlAnalysis}
              assessmentId={selectedAssessment?.id ?? null}
              isLatestAssessment={isLatestAssessment}
              isLoading={isLoading}
              technologyId={technologyId}
            />
            <SimilarTechnologiesTile
              assessmentId={selectedAssessment?.id ?? null}
              isLatestAssessment={isLatestAssessment}
              isLoading={isLoading}
              provenance={trlAnalysis?.provenance?.similarTechnologiesSummary}
              similarTechnologies={similarTechnologies}
              technologyId={technologyId}
            />
          </div>
        </div>
      </div>

      <StartTRLModal
        isOpen={isTrlModalOpen}
        onClose={() => setIsTrlModalOpen(false)}
        technology={technology}
      />
    </>
  );
};
